import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import SideRenderer from "../components/SideRenderer";
import RatingButtons from "../components/RatingButtons";
import DrawingPad, { getStoredPadEnabled, setStoredPadEnabled } from "../components/DrawingPad";
import type { Rating, StudyCard, StudyQueue } from "@glosor/shared";

const QUICK_BUMPS = [5, 10, 20];

export default function Study() {
  const { deckId } = useParams();
  const id = Number(deckId);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();

  const [revealed, setRevealed] = useState(false);
  const [card, setCard] = useState<StudyCard | null>(null);
  const [queue, setQueue] = useState<StudyQueue | null>(null);
  const [bump, setBump] = useState(10);
  const [padEnabled, setPadEnabled] = useState(getStoredPadEnabled);
  // Bumped on every queue change so the pad remounts blank for the next card
  // (cardId alone isn't enough — "Again" can re-serve the same card).
  const [padSeq, setPadSeq] = useState(0);

  const initial = useQuery({
    queryKey: ["study", id],
    queryFn: () => api.studyNext(id),
    enabled: Number.isInteger(id),
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (initial.data) {
      applyQueue(initial.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.data]);

  function applyQueue(q: StudyQueue) {
    setCard(q.next);
    setQueue(q);
    setRevealed(false);
    setPadSeq((s) => s + 1);
  }

  function togglePad() {
    const next = !padEnabled;
    setPadEnabled(next);
    setStoredPadEnabled(next);
  }

  const review = useMutation({
    mutationFn: (vars: { cardId: number; rating: Rating }) =>
      api.review({ cardId: vars.cardId, rating: vars.rating }),
    onSuccess: (q) => {
      applyQueue(q);
      qc.invalidateQueries({ queryKey: ["decks"] });
    },
  });

  const increaseNew = useMutation({
    mutationFn: (amount: number) => api.increaseNewToday(id, amount),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["decks"] });
      const fresh = await api.studyNext(id);
      applyQueue(fresh);
    },
  });

  const deleteNote = useMutation({
    mutationFn: (noteId: number) => api.deleteNote(noteId),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["notes", id] });
      qc.invalidateQueries({ queryKey: ["decks"] });
      const fresh = await api.studyNext(id);
      applyQueue(fresh);
    },
  });

  const undo = useMutation({
    mutationFn: () => api.undoReview(id),
    onSuccess: (q) => {
      applyQueue(q);
      qc.invalidateQueries({ queryKey: ["decks"] });
    },
  });

  function onRate(rating: Rating) {
    if (!card || review.isPending) return;
    review.mutate({ cardId: card.cardId, rating });
  }

  // Latest-ref so the keydown handler always sees the current state and
  // callbacks (avoids the stale-closure bug from registering once at mount).
  const onRateRef = useRef(onRate);
  onRateRef.current = onRate;
  const revealedRef = useRef(revealed);
  revealedRef.current = revealed;
  const cardRef = useRef(card);
  cardRef.current = card;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // e.repeat: a held space bar must not reveal and then instantly rate.
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (e.key === " " || e.key === "Enter") {
        // With a button (or other control) focused, stay out of the way — the
        // browser activates it natively, which is how the autoFocus'd
        // Show answer/Good buttons normally handle Space/Enter. This branch is
        // the fallback for when that focus never got applied (e.g. arriving on
        // the page with focus still outside the document).
        const target = e.target instanceof Element ? e.target : null;
        if (target?.closest("button, a, input, textarea, select")) return;
        if (!cardRef.current) return;
        e.preventDefault();
        if (revealedRef.current) onRateRef.current(3);
        else setRevealed(true);
        return;
      }
      if (!revealedRef.current) return;
      if (e.key < "1" || e.key > "4") return;
      e.preventDefault();
      onRateRef.current(Number(e.key) as Rating);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!Number.isInteger(id)) return <div className="p-6 text-slate-500 dark:text-slate-400">Bad deck id.</div>;
  if (initial.isLoading) return <div className="p-6 text-slate-500 dark:text-slate-400">Loading…</div>;

  const dueCount = queue?.dueCount ?? 0;
  const newCount = queue?.newCount ?? 0;
  const newToday = queue?.newCardsStudiedToday ?? 0;
  const newCap = queue?.effectiveDailyNewCards ?? 0;
  const extraToday = queue?.extraNewToday ?? 0;
  const canUndo = (queue?.canUndo ?? false) && !undo.isPending && !review.isPending;
  // If we have no card to show, the queue is empty AND there are state=0 cards waiting,
  // the new-card cap is what's blocking us. Offer to bump it inline.
  const capBlocking = !card && dueCount === 0 && newCount > 0;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-4 min-h-full flex flex-col">
      <header className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400 gap-2">
        <button onClick={() => navigate(`/decks/${id}`)} className="btn-secondary">←</button>
        <div className="flex items-center gap-3">
          <div>
            Due: <span className="text-slate-900 dark:text-slate-200">{dueCount}</span>
            {"  ·  "}New: <span className="text-slate-900 dark:text-slate-200">{newCount}</span>
            {newCap > 0 && (
              <>
                {"  ·  "}Today:{" "}
                <span className="text-slate-900 dark:text-slate-200">
                  {newToday}/{newCap}
                </span>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => undo.mutate()}
            disabled={!canUndo}
            title="Revert last review"
            className="btn-secondary text-sm disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ↶ Undo
          </button>
        </div>
      </header>

      {!card ? (
        <div className="card-surface p-8 text-center space-y-4 flex-1 flex flex-col justify-center">
          <h2 className="text-xl font-semibold">All done for now</h2>
          {capBlocking ? (
            <>
              <p className="text-slate-500 dark:text-slate-400">
                Reached today's new-card limit ({newToday}/{newCap}). {newCount} new card
                {newCount === 1 ? "" : "s"} waiting.
              </p>
              <div className="flex flex-wrap justify-center items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={9999}
                  value={bump}
                  onChange={(e) =>
                    setBump(Math.max(1, Math.min(9999, Math.floor(Number(e.target.value) || 0))))
                  }
                  className="input w-24"
                />
                <button
                  type="button"
                  onClick={() => increaseNew.mutate(extraToday + bump)}
                  disabled={increaseNew.isPending}
                  className="btn-primary"
                >
                  + Add for today
                </button>
                {QUICK_BUMPS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => increaseNew.mutate(extraToday + n)}
                    disabled={increaseNew.isPending}
                    className="btn-secondary text-sm"
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-slate-500 dark:text-slate-400">No more cards due in this deck right now.</p>
          )}
          <button onClick={() => navigate("/")} className="btn-secondary self-center">
            Back to decks
          </button>
        </div>
      ) : (
        <>
          <div className="card-surface relative p-6 sm:p-8 flex-1 flex flex-col">
            {/* Above the ink overlay so Draw/Edit/Delete stay clickable. */}
            <div className="relative z-10 flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {card.direction === "front_to_back" ? "Front → Back" : "Back → Front"}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={togglePad}
                  aria-pressed={padEnabled}
                  title="Toggle handwriting practice pad"
                  className={`text-sm ${
                    padEnabled
                      ? "text-sky-600 dark:text-sky-400"
                      : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  Draw
                </button>
                <button
                  onClick={() =>
                    navigate(`/decks/${id}/notes/${card.noteId}`, {
                      state: { from: location.pathname },
                    })
                  }
                  className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (confirm("Delete this card?")) deleteNote.mutate(card.noteId);
                  }}
                  disabled={deleteNote.isPending}
                  className="text-slate-500 hover:text-rose-500 dark:hover:text-rose-400 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
            <div className="flex-1 flex items-center justify-center">
              <SideRenderer side={card.question} />
            </div>
            {revealed && (
              <>
                <hr className="my-6 border-slate-200 dark:border-slate-700" />
                <div className="flex-1 flex items-center justify-center">
                  <SideRenderer side={card.answer} />
                </div>
                {(card.description.text || card.description.image) && (
                  <>
                    <hr className="my-4 border-slate-200 dark:border-slate-800" />
                    <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                      Description
                    </div>
                    <SideRenderer side={card.description} small />
                  </>
                )}
              </>
            )}
            {padEnabled && <DrawingPad key={padSeq} />}
          </div>

          {!revealed ? (
            <button
              autoFocus
              onClick={() => setRevealed(true)}
              className="btn-primary w-full text-lg py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-white/80 dark:focus-visible:ring-offset-slate-900"
            >
              Show answer
            </button>
          ) : (
            <RatingButtons onRate={onRate} disabled={review.isPending} preview={card.preview} />
          )}
        </>
      )}
    </div>
  );
}
